/**
 * Local save export / import (MVP v0.2 §2 "Local save + JSON export/import"; WP10).
 * One JSON file carries settings, campaign progress, the Gauntlet best and (optionally) the stored
 * telemetry sessions. Storage is injected so the round-trip is unit-tested without a DOM.
 */
import { GAME_VERSION, BUILD_STAMP } from './config.ts';
import { GAUNTLET_BEST_KEY, PROGRESS_KEY, type LevelProgress, type Progress } from './progress.ts';
import { DEFAULT_SETTINGS, SETTINGS_KEY, type Settings } from './settings.ts';
import { TELEMETRY_KEY, type Session } from './telemetry/telemetry.ts';

export const SAVE_VERSION = 1;

/** Minimal key-value store (localStorage in the app, a Map in tests). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SaveFile {
  kind: 'sackfall-save';
  version: number;
  exportedAt: string;
  gameVersion: string;
  build: string;
  settings: Settings;
  progress: Progress;
  gauntletBest: number;
  /** Included when exported with telemetry; testers send this one back. */
  telemetry?: Session[];
}

export interface ImportResult {
  ok: boolean;
  error?: string;
  /** What changed, for the confirmation line. */
  summary?: { levels: number; sessions: number; gauntletBest: number; mode: 'merge' | 'replace' };
}

function readJson<T>(store: KeyValueStore, key: string, fallback: T): T {
  try {
    const raw = store.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function exportSave(store: KeyValueStore, opts: { telemetry?: boolean } = {}): SaveFile {
  const file: SaveFile = {
    kind: 'sackfall-save',
    version: SAVE_VERSION,
    exportedAt: new Date().toISOString(),
    gameVersion: GAME_VERSION,
    build: BUILD_STAMP,
    settings: { ...DEFAULT_SETTINGS, ...readJson<Partial<Settings>>(store, SETTINGS_KEY, {}) },
    progress: readJson<Progress>(store, PROGRESS_KEY, { levels: {} }),
    gauntletBest: Number(store.getItem(GAUNTLET_BEST_KEY) ?? 0) || 0,
  };
  if (opts.telemetry) file.telemetry = readJson<Session[]>(store, TELEMETRY_KEY, []);
  return file;
}

export function exportSaveJson(store: KeyValueStore, opts: { telemetry?: boolean } = {}): string {
  return JSON.stringify(exportSave(store, opts), null, 2);
}

/** Validate a parsed file. Returns the error line, or null when it is a save we can import. */
export function validateSave(v: unknown): string | null {
  if (!v || typeof v !== 'object') return 'not a JSON object';
  const f = v as Partial<SaveFile>;
  if (f.kind !== 'sackfall-save') return 'not a Sackfall save file';
  if (typeof f.version !== 'number' || f.version > SAVE_VERSION) return `save version ${String(f.version)} is newer than this build supports (${SAVE_VERSION})`;
  if (!f.progress || typeof f.progress !== 'object' || typeof (f.progress as Progress).levels !== 'object') return 'missing progress';
  for (const [id, lp] of Object.entries((f.progress as Progress).levels)) {
    const l = lp as Partial<LevelProgress>;
    if (typeof l.cleared !== 'boolean' || typeof l.stars !== 'number' || l.stars < 0 || l.stars > 3) return `bad level record for ${id}`;
  }
  if (f.settings !== undefined && (typeof f.settings !== 'object' || f.settings === null)) return 'bad settings';
  if (f.telemetry !== undefined && !Array.isArray(f.telemetry)) return 'bad telemetry';
  return null;
}

function mergeLevel(a: LevelProgress, b: LevelProgress): LevelProgress {
  return {
    cleared: a.cleared || b.cleared,
    stars: Math.max(a.stars, b.stars) as LevelProgress['stars'],
    bestScore: Math.max(a.bestScore ?? 0, b.bestScore ?? 0),
    attempts: Math.max(a.attempts ?? 0, b.attempts ?? 0),
  };
}

/**
 * Import a save. 'merge' (default) never loses progress: cleared OR, stars and scores max, telemetry
 * sessions unioned by id. 'replace' overwrites everything the file carries.
 */
export function importSave(store: KeyValueStore, text: string, mode: 'merge' | 'replace' = 'merge'): ImportResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: 'not valid JSON' };
  }
  const err = validateSave(parsed);
  if (err) return { ok: false, error: err };
  const file = parsed as SaveFile;
  const current = exportSave(store, { telemetry: true });
  const progress: Progress = { levels: {} };
  if (mode === 'merge') {
    const ids = new Set([...Object.keys(current.progress.levels), ...Object.keys(file.progress.levels)]);
    for (const id of ids) {
      const a = current.progress.levels[id];
      const b = file.progress.levels[id];
      progress.levels[id] = a && b ? mergeLevel(a, b) : { ...(a ?? b) };
    }
    const ka = current.progress.kit;
    const kb = file.progress.kit;
    if (ka || kb) progress.kit = { shield: Math.max(ka?.shield ?? 0, kb?.shield ?? 0), turbo: Math.max(ka?.turbo ?? 0, kb?.turbo ?? 0), beam: Math.max(ka?.beam ?? 0, kb?.beam ?? 0) };
    const seen = { ...(current.progress.seen ?? {}), ...(file.progress.seen ?? {}) };
    if (Object.keys(seen).length) progress.seen = seen;
  } else {
    for (const [id, l] of Object.entries(file.progress.levels)) progress.levels[id] = { ...l };
    if (file.progress.kit) progress.kit = { ...file.progress.kit };
    if (file.progress.seen) progress.seen = { ...file.progress.seen };
  }
  const gauntletBest = mode === 'merge' ? Math.max(current.gauntletBest, file.gauntletBest ?? 0) : file.gauntletBest ?? 0;
  let sessions = current.telemetry ?? [];
  if (file.telemetry) {
    if (mode === 'merge') {
      const seen = new Set(sessions.map((s) => s.sessionId));
      sessions = [...sessions, ...file.telemetry.filter((s) => !seen.has(s.sessionId))].slice(-50);
    } else sessions = file.telemetry.slice(-50);
  }
  store.setItem(PROGRESS_KEY, JSON.stringify(progress));
  store.setItem(GAUNTLET_BEST_KEY, String(gauntletBest));
  if (file.settings) store.setItem(SETTINGS_KEY, JSON.stringify({ ...DEFAULT_SETTINGS, ...file.settings }));
  if (file.telemetry) store.setItem(TELEMETRY_KEY, JSON.stringify(sessions));
  return { ok: true, summary: { levels: Object.keys(progress.levels).length, sessions: file.telemetry ? sessions.length : 0, gauntletBest, mode } };
}
